import {
  type ButtonHTMLAttributes,
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
} from "react";
import { Spinner } from "./spinner";
import { cn } from "./utils";

const variants = {
  primary: "bg-accent text-accent-fg hover:bg-accent-hover shadow-sm",
  secondary: "bg-bg text-fg border border-border-strong hover:bg-muted shadow-sm",
  ghost: "text-fg-muted hover:text-fg hover:bg-muted",
  danger: "bg-danger text-white hover:opacity-90 shadow-sm",
  "danger-ghost": "text-danger hover:bg-danger-soft",
} as const;

const sizes = {
  sm: "h-7 px-2.5 text-[12.5px] gap-1.5",
  md: "h-8 px-3 text-[13px] gap-2",
  lg: "h-10 px-4 text-sm gap-2",
  icon: "h-8 w-8 justify-center",
  "icon-sm": "h-7 w-7 justify-center",
} as const;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
  asChild?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = "secondary", size = "md", loading, asChild, children, disabled, ...props },
    ref,
  ) => {
    const classes = cn(
      "inline-flex shrink-0 items-center rounded-md font-medium whitespace-nowrap transition-colors select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
      variants[variant],
      sizes[size],
      className,
    );
    // asChild styles the single child (a link) as a button instead of nesting it in one.
    if (asChild && isValidElement(children)) {
      const child = children as ReactElement<{ className?: string }>;
      return cloneElement(child, { className: cn(classes, child.props.className) });
    }
    return (
      <button ref={ref} className={classes} disabled={disabled || loading} {...props}>
        {loading && <Spinner />}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
